import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';
import api from '../services/api';
import Badge from '../components/Badge';
import SLAIndicator from '../components/SLAIndicator';
import Button from '../components/Button';
import type { DashboardSummary, Ticket, Role, Task } from '../types';
import { ROLE_LABELS } from '../utils/constants';
import { hasRole } from '../utils/roleUtils';

const COLORS = ['#26148C','#5B21FF','#A7A1FF','#86E9FF','#FF31B0'];

interface WelcomeState { name: string; role: string }

type Period = 'day' | 'week' | 'month' | 'all';
const PERIODS: Array<{ value: Period; label: string }> = [
  { value: 'day',   label: 'Daily' },
  { value: 'week',  label: 'Weekly' },
  { value: 'month', label: 'Monthly' },
  { value: 'all',   label: 'All Time' },
];

// Where a stat card sends the user when clicked — routed to whichever tab they
// actually have access to (RoleGuard-wise) and, where the target page supports it,
// pre-filtered to the matching status via router state (see Tickets.tsx/AllTickets.tsx,
// which seed their status filter from location.state.status on mount).
function cardTarget(label: string, user: { role: string; roles?: string[] } | null | undefined):
  { path: string; state?: { status: string } } {
  // hasRole() treats admin as holding every role, so admin/it_manager must be
  // checked first — otherwise an admin would always match the first branch tried.
  const isMgr = hasRole(user, 'admin') || hasRole(user, 'it_manager');
  const isLM  = hasRole(user, 'line_manager') || hasRole(user, 'top_management');
  const isIT  = hasRole(user, 'it_staff');

  switch (label) {
    case 'Total Tickets':
      return isMgr ? { path: '/all-tickets' } : { path: '/tickets' };
    case 'Pending Approval':
      if (isMgr) return { path: '/all-tickets', state: { status: 'PendingITManager' } };
      if (isLM)  return { path: '/approvals' };
      return { path: '/tickets' };
    case 'In Progress':
      if (isMgr) return { path: '/all-tickets', state: { status: 'InProgress' } };
      if (isIT)  return { path: '/workspace' };
      return { path: '/tickets', state: { status: 'InProgress' } };
    case 'Completed':
      return isMgr
        ? { path: '/all-tickets', state: { status: 'Completed' } }
        : { path: '/tickets',     state: { status: 'Completed' } };
    case 'SLA Breached':
      return (isMgr || isIT) ? { path: '/workspace' } : { path: '/tickets' };
    default:
      return { path: '/tickets' };
  }
}

// Mirrors the backend's periodCutoff() so the client-side Tasks widget filters to the
// same window the server-side ticket stats use for the same period selection.
function periodCutoffMs(period: Period): number {
  const now = Date.now();
  if (period === 'day')   return new Date().setHours(0, 0, 0, 0);
  if (period === 'week')  return now - 7  * 86_400_000;
  if (period === 'month') return now - 30 * 86_400_000;
  return 0;
}

export default function Dashboard() {
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [recent,  setRecent]  = useState<Ticket[]>([]);
  const [welcome, setWelcome] = useState<WelcomeState | null>(null);
  const [loading, setLoading] = useState(true);
  const [tasks,   setTasks]   = useState<Task[]>([]);
  const [period,  setPeriod]  = useState<Period>('all');
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  // Tasks graph only applies to roles that actually have the Tasks feature
  const hasTasksFeature = hasRole(user, 'it_staff') || hasRole(user, 'it_manager') || hasRole(user, 'admin');

  // Read welcome state from router once on mount, then clear it so refresh doesn't re-show
  useEffect(() => {
    const ws = (location.state as any)?.welcome as WelcomeState | undefined;
    if (!ws) return;
    setWelcome(ws);
    navigate('.', { replace: true, state: {} });
    const t = setTimeout(() => setWelcome(null), 8000);
    return () => clearTimeout(t);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get<DashboardSummary>('/reports/summary', { params: { period } }).then(r => setSummary(r.data)),
      api.get<Ticket[]>('/tickets', { params: { limit: 5 } })
        .then(r => setRecent(Array.isArray(r.data) ? r.data : (r.data as any).data ?? [])),
    ]).finally(() => setLoading(false));
  }, [period]);

  // Same source Tasks.tsx uses (GET /tasks) — the completed count here always matches
  // the "Completed Tasks" count on the Tasks tab, including both ticket-linked and
  // manually-logged tasks.
  useEffect(() => {
    if (!hasTasksFeature) return;
    api.get<Task[]>('/tasks').then(r => setTasks(r.data)).catch(() => setTasks([]));
  }, [hasTasksFeature]);

  const cards = summary ? [
    { label: 'Total Tickets',    value: summary.total,       bg: '#010E23', fg: '#fff' },
    { label: 'Pending Approval', value: summary.pending,     bg: '#26148C', fg: '#fff' },
    { label: 'In Progress',      value: summary.inProgress,  bg: '#0A1F45', fg: '#fff' },
    { label: 'Completed',        value: summary.completed,   bg: '#1A7A3C', fg: '#fff' },
    { label: 'SLA Breached',     value: summary.slaBreached, bg: '#B91C1C', fg: '#fff' },
  ] : [];

  const chartData = summary?.byStatus
    ? Object.entries(summary.byStatus).map(([name, count]) => ({ name, count }))
    : [];

  const periodStart = periodCutoffMs(period);
  const inPeriod = (t: Task) => {
    if (!periodStart) return true;
    const ts = new Date(t.updatedAt ?? t.taskDate ?? t.createdAt ?? 0).getTime();
    return ts >= periodStart;
  };
  const completedTasks   = tasks.filter(t => t.status === 'completed' && inPeriod(t));
  const activeTasksCount = tasks.filter(t => t.status !== 'completed').length;
  const taskChartData = Object.entries(
    completedTasks.reduce((acc: Record<string, number>, t) => {
      acc[t.category] = (acc[t.category] ?? 0) + 1;
      return acc;
    }, {}),
  ).map(([name, count]) => ({ name, count }));

  const roleLabel = welcome ? (ROLE_LABELS[welcome.role as Role] ?? welcome.role) : '';

  if (user && !user.role) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '80px 24px', textAlign: 'center' }}>
        <div style={{ maxWidth: 480 }}>
          <h2 style={{ fontSize: 20, fontWeight: 700, color: '#010E23', marginBottom: 12 }}>Welcome to ServiceDesk</h2>
          <p style={{ fontSize: 14, color: '#64748b', marginBottom: 8, lineHeight: 1.6 }}>
            Your account has been created. An administrator needs to assign your role before you can access the portal.
          </p>
          <p style={{ fontSize: 14, color: '#64748b' }}>
            Please contact:{' '}
            <a href="mailto:kayum.khaja@permus.io" style={{ color: '#26148C', fontWeight: 600 }}>
              kayum.khaja@permus.io
            </a>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">

      {/* Welcome banner */}
      {welcome && (
        <div className="relative overflow-hidden rounded-xl bg-gradient-to-r from-[#010E23] to-[#26148C] text-white px-5 py-4 flex items-start justify-between shadow-lg">
          <div className="pr-6">
            <p className="text-sm font-semibold">
              Welcome to ServiceDesk, {welcome.name}!
            </p>
            <p className="text-xs text-white/70 mt-1 leading-relaxed">
              You&apos;re logged in as <span className="text-white font-medium">{roleLabel}</span>.
              {welcome.role === 'employee' && (
                <> Need a different role? Contact your IT Admin at{' '}
                  <a href="mailto:it-support@permus.io" className="underline underline-offset-2 text-white/80 hover:text-white">
                    it-support@permus.io
                  </a>.
                </>
              )}
            </p>
          </div>
          <button
            onClick={() => setWelcome(null)}
            className="absolute top-3 right-4 text-white/50 hover:text-white text-xl leading-none transition-colors"
            aria-label="Dismiss welcome message"
          >
            ×
          </button>
          <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-white/15">
            <div
              className="h-full bg-white/35 origin-left"
              style={{ animation: 'welcomeBar 8s linear forwards' }}
            />
          </div>
          <style>{`
            @keyframes welcomeBar { from { transform: scaleX(1); } to { transform: scaleX(0); } }
          `}</style>
        </div>
      )}

      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-lg font-bold text-[#010E23]">Dashboard</h1>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-lg border border-gray-200 bg-white p-0.5">
            {PERIODS.map(p => (
              <button
                key={p.value}
                onClick={() => setPeriod(p.value)}
                className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
                  period === p.value ? 'bg-[#26148C] text-white' : 'text-gray-500 hover:text-gray-700'}`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <Button onClick={() => navigate('/tickets/new')} size="sm">+ New Request</Button>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {loading
          ? Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="rounded-xl px-4 py-4 shadow-sm bg-gray-100 animate-pulse h-[68px]" />
            ))
          : cards.map(c => {
              const target = cardTarget(c.label, user);
              return (
                <div
                  key={c.label}
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate(target.path, target.state ? { state: target.state } : undefined)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      navigate(target.path, target.state ? { state: target.state } : undefined);
                    }
                  }}
                  className="rounded-xl px-4 py-4 shadow-sm cursor-pointer transition-transform hover:-translate-y-0.5 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-white/60"
                  style={{ background: c.bg }}
                >
                  <p className="text-2xl font-bold" style={{ color: c.fg }}>{c.value}</p>
                  <p className="text-xs mt-0.5" style={{ color: '#fff' }}>{c.label}</p>
                </div>
              );
            })
        }
      </div>

      {/* Chart + Recent */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <h2 className="text-sm font-semibold text-[#010E23] mb-4">
            Tickets by Status <span className="text-gray-400 font-normal">— {PERIODS.find(p => p.value === period)?.label}</span>
          </h2>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={chartData} margin={{ top: 0, right: 0, bottom: 0, left: -20 }}>
              <XAxis dataKey="name" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip />
              <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                {chartData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <h2 className="text-sm font-semibold text-[#010E23] mb-3">Recent Tickets</h2>
          <div className="space-y-3">
            {loading
              ? Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="h-10 bg-gray-100 rounded-lg animate-pulse" />
                ))
              : recent.length === 0
                ? <p className="text-xs text-gray-400 text-center py-4">No tickets yet</p>
                : recent.map(t => (
                    <div
                      key={t.id}
                      className="flex items-center justify-between cursor-pointer hover:bg-gray-50 rounded-lg p-2 -mx-2 transition-colors"
                      onClick={() => navigate(`/tickets/${t.id}`)}
                    >
                      <div className="min-w-0">
                        <p className="text-xs font-medium text-[#010E23] truncate">{t.id}</p>
                        <p className="text-[10px] text-gray-400 truncate">{t.requester?.name || '—'}</p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <SLAIndicator ticket={t} compact />
                        <Badge status={t.status} small />
                      </div>
                    </div>
                  ))
            }
          </div>
        </div>
      </div>

      {/* Tasks graph — only for roles with the Tasks feature (IT staff / IT manager / admin) */}
      {hasTasksFeature && (
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-[#010E23]">
              My Completed Tasks by Category <span className="text-gray-400 font-normal">— {PERIODS.find(p => p.value === period)?.label}</span>
            </h2>
            <div className="flex gap-4 text-xs text-gray-400">
              <span><b className="text-emerald-600">{completedTasks.length}</b> completed</span>
              <span><b className="text-[#010E23]">{activeTasksCount}</b> active</span>
            </div>
          </div>
          {taskChartData.length === 0 ? (
            <p className="text-xs text-gray-400 text-center py-8">No completed tasks yet</p>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={taskChartData} margin={{ top: 0, right: 0, bottom: 0, left: -20 }}>
                <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                  {taskChartData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      )}
    </div>
  );
}
